const SESSION_KEY = "tab_session_id";
const PERSISTENCE_KEY = "tab_session_persistence";
export const BOOKING_ACTIVE_TABS_STORAGE_KEY = "booking_active_tabs";
export const BOOKING_ACTIVE_TAB_HEARTBEAT_PREFIX = "booking_active_tab_heartbeat_";
export const BOOKING_ACTIVE_TAB_STALE_THRESHOLD_MS = 4000;

export function isBookingTabSessionActive(sessionId: string | null | undefined): boolean {
  if (
    !sessionId ||
    typeof globalThis.window === "undefined" ||
    typeof globalThis.localStorage === "undefined"
  ) {
    return false;
  }

  try {
    const storedTabs = globalThis.localStorage.getItem(BOOKING_ACTIVE_TABS_STORAGE_KEY);
    if (!storedTabs) {
      return false;
    }

    const parsedTabs: unknown = JSON.parse(storedTabs);
    if (!Array.isArray(parsedTabs) || !parsedTabs.includes(sessionId)) {
      return false;
    }

    const heartbeatValue = globalThis.localStorage.getItem(
      `${BOOKING_ACTIVE_TAB_HEARTBEAT_PREFIX}${sessionId}`,
    );
    const lastSeen = heartbeatValue ? Number.parseInt(heartbeatValue, 10) : Number.NaN;

    return (
      !Number.isNaN(lastSeen) && Date.now() - lastSeen <= BOOKING_ACTIVE_TAB_STALE_THRESHOLD_MS
    );
  } catch {
    return false;
  }
}

/**
 * Gets the current tab session ID.
 *
 * The session ID is stored in window.name to persist across different ports and subdomains
 * within the same tab. SessionStorage is used as a fast-access cache.
 *
 * Priority:
 * 1. Check window.name (persists across origins in the same tab)
 * 2. Check sessionStorage (fast access, but origin-specific)
 * 3. Generate new ID if neither exists
 *
 * @returns The tab session ID, or null if running on server
 */
export function getTabSessionId(): string | null {
  if (typeof globalThis.window === "undefined") {
    return null;
  }

  // Try to get from window.name first (cross-origin persistent)
  const windowName = globalThis.window.name;

  // Read whichever sessionStorage key is present (new or legacy)
  const sessionStorageId =
    typeof globalThis.sessionStorage !== "undefined"
      ? globalThis.sessionStorage.getItem(SESSION_KEY) || ""
      : "";

  // Check for persistence cookie to validate window.name
  const hasPersistenceCookie =
    typeof document !== "undefined" &&
    document.cookie.split(";").some((c) => c.trim().startsWith(`${PERSISTENCE_KEY}=`));

  // If window.name is set but cookie is missing AND sessionStorage is empty,
  // it implies "Clear site data" was used. Clear window.name to reset.
  if (windowName && !hasPersistenceCookie && !sessionStorageId) {
    console.warn("Detected stale window.name after site data clear. Resetting session.");
    globalThis.window.name = "";
    return null;
  }

  // Heal cookie if we have a valid sessionStorage (fixes existing sessions)
  if (sessionStorageId && !hasPersistenceCookie && typeof document !== "undefined") {
    document.cookie = `${PERSISTENCE_KEY}=true; path=/; SameSite=Lax`;
  }

  const tabId = sessionStorageId || windowName;

  // Overwritten whenever it disagrees, not only when empty, so a value left behind by
  // another origin cannot outlive this call.
  if (tabId && globalThis.window.name !== tabId) {
    globalThis.window.name = tabId;
  }

  if (tabId && typeof globalThis.sessionStorage !== "undefined") {
    const sessionId = globalThis.sessionStorage.getItem(SESSION_KEY);
    if (sessionId !== tabId) {
      globalThis.sessionStorage.setItem(SESSION_KEY, tabId);
    }
  }

  return tabId || null;
}

/**
 * Sets the tab session ID in both window.name and sessionStorage.
 *
 * @param id - The tab session ID to set
 */
export function setTabSessionId(id: string): void {
  if (typeof globalThis.window === "undefined") {
    return;
  }

  // Set in window.name for cross-origin persistence
  globalThis.window.name = id;

  // Also set in sessionStorage for fast access
  if (typeof globalThis.sessionStorage !== "undefined") {
    globalThis.sessionStorage.setItem(SESSION_KEY, id);
  }

  if (typeof document !== "undefined") {
    // Set persistence flag
    document.cookie = `${PERSISTENCE_KEY}=true; path=/; SameSite=Lax`;

    // CRITICAL: Set the actual session ID cookie for middleware
    document.cookie = `${SESSION_KEY}=${id}; path=/; SameSite=Lax`;

    // Dispatch event for any listeners (like TabSessionSync or waitForSessionId)
    const event = new CustomEvent("session-initialized", { detail: { sessionId: id } });
    globalThis.window.dispatchEvent(event);
  }
}

export const TAB_SESSION_QUERY_PARAM = "tabSessionId";

export function resolveTabSessionId(): string | null {
  if (typeof globalThis.window === "undefined") {
    return null;
  }

  const existing = getTabSessionId();
  if (existing) {
    return existing;
  }

  const fromUrl = new URLSearchParams(globalThis.window.location.search).get(
    TAB_SESSION_QUERY_PARAM,
  );

  if (!fromUrl) {
    return null;
  }

  setTabSessionId(fromUrl);

  return fromUrl;
}

/**
 * Clears the current tab session ID from both window.name and sessionStorage.
 */
export function clearTabSessionId(): void {
  if (typeof globalThis.window === "undefined") {
    return;
  }

  globalThis.window.name = "";

  if (typeof globalThis.sessionStorage !== "undefined") {
    globalThis.sessionStorage.removeItem(SESSION_KEY);
  }

  if (typeof document !== "undefined") {
    document.cookie = `${PERSISTENCE_KEY}=; path=/; max-age=0; SameSite=Lax`;
    document.cookie = `${SESSION_KEY}=; path=/; max-age=0; SameSite=Lax`;
  }
}

/**
 * Waits for the session ID to be initialized.
 * This is useful for API calls that need to wait until session setup is complete.
 *
 * @param timeoutMs - Maximum time to wait in milliseconds (default: 12000)
 * @returns Promise that resolves with the session ID or null if timeout/error
 */
export function waitForSessionId(timeoutMs: number = 12000): Promise<string | null> {
  if (typeof globalThis.window === "undefined") {
    return Promise.resolve(null);
  }

  // If session ID already exists, return immediately
  const existingId = getTabSessionId();
  if (existingId) {
    return Promise.resolve(existingId);
  }

  // Otherwise, wait for the session-initialized event
  return new Promise<string | null>((resolve) => {
    const timeout = setTimeout(() => {
      console.warn("[waitForSessionId] Timeout waiting for session initialization");
      resolve(null);
    }, timeoutMs);

    const handleSessionInit = (event: Event) => {
      clearTimeout(timeout);
      const customEvent = event as CustomEvent;

      if (customEvent.detail?.error) {
        console.error("[waitForSessionId] Session initialization failed");
        resolve(null);
      } else {
        const sessionId = getTabSessionId();
        resolve(sessionId);
      }
    };

    globalThis.window.addEventListener("session-initialized", handleSessionInit, { once: true });
  });
}
